import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Package, MapPin, Phone, Truck, Calendar, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TeamLeadService } from '../../services/teamLeadService';

const DeliveredCustomers = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: deliveredData, isLoading } = useQuery({
    queryKey: ['teamLeadDeliveredCustomers', user?.id, startDate, endDate],
    queryFn: async () => {
      // Fetch booked orders and filter for delivered status
      const result = await TeamLeadService.getBookedOrders(user?.id);
      if (result.success && result.data) {
        return result.data.filter(
          (order) => 
            order.current_status_type === 'DELIVERED' ||
            order.status === 'delivered' ||
            order.shipping_status === 'delivered'
        );
      }
      return [];
    },
    enabled: !!user?.id,
  });

  const deliveredCustomers = deliveredData || [];

  const filteredCustomers = deliveredCustomers.filter((customer) => {
    const matchesSearch = !searchQuery ||
      customer.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.contact_number?.includes(searchQuery) ||
      customer.order_id?.includes(searchQuery);
    
    const orderDate = customer.created_at || customer.order_created_at;
    const matchesDate = !orderDate || (
      orderDate >= startDate &&
      orderDate <= endDate
    );

    return matchesSearch && matchesDate;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  const openTracking = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/teamlead/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Delivered Customers</h1>
          <p className="text-gray-600 mt-1">View delivered customer information</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, phone, or order ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Delivered</p>
              <p className="text-2xl font-bold text-gray-900">{filteredCustomers.length}</p>
            </div>
            <CheckCircle size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{filteredCustomers.reduce((sum, c) => sum + (Number(c.amount || c.final_amount) || 0), 0).toLocaleString('en-IN')}
              </p>
            </div>
            <Package size={24} className="text-purple-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">This Period</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Date(startDate).toLocaleDateString('en-IN', { month: 'short' })} - {new Date(endDate).toLocaleDateString('en-IN', { month: 'short' })}
              </p>
            </div>
            <Calendar size={24} className="text-blue-600" />
          </div>
        </div>
      </div>

      {/* Delivered Customers List */}
      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading delivered customers...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <CheckCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No delivered customers found for selected period</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCustomers.map((customer) => (
            <div key={customer.order_id || customer.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle size={24} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {customer.customer_name || 'Unknown Customer'}
                      </h3>
                      <p className="text-sm text-gray-600">{customer.contact_number || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Order ID</p>
                      <p className="text-sm font-medium text-gray-900">
                        #{customer.order_id?.slice(0, 8) || customer.id?.slice(0, 8)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Delivery Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(customer.sr_status_raw_time || customer.delivered_at || customer.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Address</p>
                      <div className="flex items-start space-x-2">
                        <MapPin size={16} className="text-gray-400 mt-0.5" />
                        <p className="text-sm text-gray-900">
                          {customer.address || customer.delivery_address || 'N/A'}
                          {customer.pincode && ` - ${customer.pincode}`}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Amount</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(customer.amount || customer.final_amount)}
                      </p>
                    </div>
                    {customer.vehicle_model && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Vehicle</p>
                        <p className="text-sm text-gray-900">
                          {customer.vehicle_model} {customer.vehicle_number ? `- ${customer.vehicle_number}` : ''}
                        </p>
                      </div>
                    )}
                    {customer.courier_name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Courier</p>
                        <p className="text-sm text-gray-900">{customer.courier_name}</p>
                        {customer.awb_code && (
                          <p className="text-xs text-gray-600">AWB: {customer.awb_code}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {customer.tracking_url && (
                <button
                  onClick={() => openTracking(customer.tracking_url)}
                  className="w-full px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center justify-center space-x-2"
                >
                  <Truck size={18} />
                  <span>Track Shipment</span>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeliveredCustomers;

